import { supabase } from "./supabase";

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Call a Supabase Edge Function. The edge function is the ONLY place that holds
 * Samsara/Fleetio/QuickManage secrets and talks to those APIs (docs/ARCHITECTURE.md
 * trust boundary). The browser never calls those external APIs directly.
 *
 * @throws ApiError when Supabase is not configured or the function errors.
 */
export async function invokeFunction<TResponse, TBody = unknown>(
  name: string,
  body?: TBody,
): Promise<TResponse> {
  if (!supabase) {
    throw new ApiError(
      "Backend not configured yet (set VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).",
      503,
    );
  }
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const functionBody =
    body && typeof body === "object" && !Array.isArray(body)
      ? { ...body, __client: { timeZone } }
      : { value: body, __client: { timeZone } };

  const { data, error } = await supabase.functions.invoke<TResponse>(name, {
    body: functionBody,
  });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx) {
      try {
        const payload = (await ctx.json()) as { error?: string; message?: string };
        const detail = payload.error ?? payload.message;
        if (detail) throw new ApiError(detail, ctx.status);
      } catch (parseErr) {
        if (parseErr instanceof ApiError) throw parseErr;
      }
    }
    throw new ApiError(error.message);
  }
  return data as TResponse;
}
